
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '@/integrations/supabase/client'

interface OperatorAuthGuardProps {
  children: React.ReactNode
}

const OperatorAuthGuard = ({ children }: OperatorAuthGuardProps) => {
  const [loading, setLoading] = useState(true)
  const [isOperatorAuthenticated, setIsOperatorAuthenticated] = useState(false)
  const navigate = useNavigate()

  useEffect(() => {
    const checkOperatorAuth = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession()
        
        if (!session) {
          console.log('No session found, redirecting to login')
          setIsOperatorAuthenticated(false)
          navigate('/operator/login', { replace: true })
          return
        }

        // Check if user is an operator
        const { data: operator, error } = await supabase
          .from('operators')
          .select('*')
          .eq('user_id', session.user.id)
          .single()

        if (error || !operator) {
          console.log('No operator profile found, redirecting to login')
          setIsOperatorAuthenticated(false)
          navigate('/operator/login', { replace: true })
          return
        }

        setIsOperatorAuthenticated(true)
      } catch (error) {
        console.error('Auth check error:', error)
        setIsOperatorAuthenticated(false)
        navigate('/operator/login', { replace: true })
      } finally {
        setLoading(false)
      }
    }

    checkOperatorAuth()

    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      console.log('Auth state changed:', event, session?.user?.id)
      
      if (event === 'SIGNED_OUT' || !session) {
        console.log('User signed out or session lost, redirecting to login')
        setIsOperatorAuthenticated(false)
        setLoading(false)
        navigate('/operator/login', { replace: true })
      } else if (event === 'SIGNED_IN' && session) {
        console.log('User signed in, re-checking operator status')
        // Re-check operator status when signed in
        checkOperatorAuth()
      }
    })

    return () => subscription.unsubscribe()
  }, [navigate])

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
      </div>
    )
  }

  return isOperatorAuthenticated ? <>{children}</> : null
}

export default OperatorAuthGuard
